/**
 * Gravador de áudio PTT.
 *
 * Estratégia (em ordem de preferência):
 *   1. opus-recorder (WASM)  → produz OGG/Opus REAL aceito pelo WhatsApp Cloud
 *      e pela Evolution. Funciona em Chrome, Firefox e Safari modernos.
 *   2. Fallback PCM/WAV via Web Audio API → último recurso (Evolution legado).
 *
 * IMPORTANTE: MediaRecorder nativo do navegador NÃO é usado para Opus,
 * porque Chrome/Edge frequentemente reportam suporte a "audio/ogg;codecs=opus"
 * mas emitem bytes em container WebM — o WhatsApp Cloud rejeita
 * (erro 131053 "Media upload error: application/octet-stream").
 */

import Recorder from "opus-recorder";
import encoderPath from "opus-recorder/dist/encoderWorker.min.js?url";

export interface OpusRecorderHandle {
  start: () => Promise<void>;
  stop: () => Promise<Blob>;
  cancel: () => void;
  isRecording: () => boolean;
}

const BUFFER_SIZE = 4096;

const encodeWav = (chunks: Float32Array[], sampleRate: number): Blob => {
  const totalSamples = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const buffer = new ArrayBuffer(44 + totalSamples * 2);
  const view = new DataView(buffer);

  const writeString = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i += 1) view.setUint8(offset + i, value.charCodeAt(i));
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + totalSamples * 2, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, totalSamples * 2, true);

  let offset = 44;
  for (const chunk of chunks) {
    for (let i = 0; i < chunk.length; i += 1) {
      const sample = Math.max(-1, Math.min(1, chunk[i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([buffer], { type: "audio/wav" });
};

function createOpusRecorderHandle(): OpusRecorderHandle {
  // opus-recorder não expõe tipagens; fazemos cast para any para a config.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let rec: any = null;
  let chunks: Uint8Array[] = [];
  let recording = false;
  let stopResolver: ((blob: Blob) => void) | null = null;

  return {
    async start() {
      if (recording) return;
      chunks = [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const RecorderCtor = Recorder as any;
      rec = new RecorderCtor({
        encoderPath,
        encoderApplication: 2048, // VOIP (compatível com WhatsApp)
        encoderSampleRate: 48000,
        originalSampleRateOverride: 48000,
        encoderBitRate: 24000,
        numberOfChannels: 1,
        streamPages: true,
        maxFramesPerPage: 40,
        encoderFrameSize: 20,
      });
      rec.ondataavailable = (typedArray: Uint8Array) => {
        // Cada chunk é uma página OGG válida; concatenar gera arquivo OGG/Opus completo.
        chunks.push(new Uint8Array(typedArray));
      };
      rec.onstop = () => {
        const blob = new Blob(chunks as BlobPart[], { type: "audio/ogg" });
        if (stopResolver) {
          stopResolver(blob);
          stopResolver = null;
        }
      };
      await rec.start();
      recording = true;
    },
    async stop() {
      if (!recording || !rec) return new Blob([], { type: "audio/ogg" });
      recording = false;
      const blobPromise = new Promise<Blob>((resolve) => {
        stopResolver = resolve;
      });
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
      const blob = await blobPromise;
      rec = null;
      return blob;
    },
    cancel() {
      recording = false;
      stopResolver = null;
      try {
        rec?.stop();
      } catch {
        /* ignore */
      }
      rec = null;
      chunks = [];
    },
    isRecording: () => recording,
  };
}

function createWavRecorderHandle(): OpusRecorderHandle {
  const AudioContextCtor =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) throw new Error("AudioContext não suportado neste navegador");

  let audioContext: AudioContext | null = null;
  let stream: MediaStream | null = null;
  let source: MediaStreamAudioSourceNode | null = null;
  let processor: ScriptProcessorNode | null = null;
  let mutedOutput: GainNode | null = null;
  let chunks: Float32Array[] = [];
  let recording = false;
  let sampleRate = 48000;

  const cleanup = () => {
    try { processor?.disconnect(); } catch { /* ignore */ }
    try { source?.disconnect(); } catch { /* ignore */ }
    try { mutedOutput?.disconnect(); } catch { /* ignore */ }
    stream?.getTracks().forEach((track) => track.stop());
    if (audioContext && audioContext.state !== "closed") audioContext.close().catch(() => {});
    processor = null;
    source = null;
    mutedOutput = null;
    stream = null;
    audioContext = null;
  };

  return {
    async start() {
      if (recording) return;
      chunks = [];
      audioContext = new AudioContextCtor({ sampleRate: 48000 });
      await audioContext.resume().catch(() => undefined);
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      source = audioContext.createMediaStreamSource(stream);
      processor = audioContext.createScriptProcessor(BUFFER_SIZE, 1, 1);
      mutedOutput = audioContext.createGain();
      mutedOutput.gain.value = 0;
      sampleRate = audioContext.sampleRate;
      processor.onaudioprocess = (event) => {
        if (!recording) return;
        chunks.push(new Float32Array(event.inputBuffer.getChannelData(0)));
      };
      source.connect(processor);
      processor.connect(mutedOutput);
      mutedOutput.connect(audioContext.destination);
      recording = true;
    },
    async stop() {
      if (!recording) return new Blob([], { type: "audio/wav" });
      recording = false;
      const blob = encodeWav(chunks, sampleRate);
      cleanup();
      return blob;
    },
    cancel() {
      recording = false;
      chunks = [];
      cleanup();
    },
    isRecording: () => recording,
  };
}

function isOpusRecorderSupported(): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return typeof (Recorder as any)?.isRecordingSupported === "function"
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ? (Recorder as any).isRecordingSupported()
      : true;
  } catch {
    return false;
  }
}

export function createOpusRecorder(): OpusRecorderHandle {
  if (typeof window === "undefined" || !navigator?.mediaDevices?.getUserMedia) {
    throw new Error("Gravação de áudio não suportada neste ambiente");
  }
  if (isOpusRecorderSupported()) {
    try {
      return createOpusRecorderHandle();
    } catch (err) {
      console.warn("[opus-recorder] init falhou, fallback para WAV:", err);
    }
  }
  return createWavRecorderHandle();
}

/** Extensão preferida derivada do MIME do blob gerado pelo recorder. */
export function audioBlobExtension(mime: string): string {
  const m = (mime || "").toLowerCase();
  if (m.includes("ogg") || m.includes("opus")) return "ogg";
  if (m.includes("mp4") || m.includes("m4a") || m.includes("aac")) return "m4a";
  if (m.includes("webm")) return "ogg";
  if (m.includes("mpeg") || m.includes("mp3")) return "mp3";
  return "wav";
}
